import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createCategoryThunk,
  getAllCategoriesThunk,
  updateCategoryByIdThunk,
  deleteCategoryByIdThunk,
  syncCategoryStatsThunk,
  getCategoryTreeThunk,
  clearCategoryError,
} from "../../store/thunks/categoryThunks";
import { setFilters, resetFilters } from "../../store/slices/categorySlice";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Dropdown from "../../components/common/Dropdown";
import Badge from "../../components/common/Badge";
import { notifyError, notifySuccess } from "../../utils/notification";
import CategoryCard from "./components/CategoryCard";
import Modal from "../../components/common/Modal";
import CategoryForm from "./components/CategoryForm";
import CategoryTreeView from "./components/CategoryTreeView";
import CategoryDetailView from "./components/CategoryDetailView";
import {
  RefreshIcon,
  ViewGridIcon,
  ViewListIcon,
  FilterIcon,
  ChartBarIcon,
  FolderOpenIcon,
  PlusIcon,
  SearchIcon,
} from "@heroicons/react/outline";

const CategoryPage = () => {
  const dispatch = useDispatch();
  const { categories, categoryTree, loading, error, filters, stats } =
    useSelector((state) => state.category);

  const [showModal, setShowModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // list, tree, grid
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [detailViewModal, setDetailViewModal] = useState({
    show: false,
    categoryId: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    category: null,
  });
  const [localFilters, setLocalFilters] = useState({
    isActive: "",
    parentId: "",
    isFeatured: "",
    showInMenu: "",
  });

  // Load categories on mount
  useEffect(() => {
    dispatch(getAllCategoriesThunk());
  }, [dispatch]);

  // Handle errors
  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearCategoryError());
    }
  }, [error, dispatch]);

  // Filter categories based on search and filters
  const filteredCategories = useMemo(() => {
    let filtered = [...categories];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (cat) =>
          cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cat.categoryId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cat.tags?.some((tag) =>
            tag.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    // Active filter
    if (localFilters.isActive !== "") {
      filtered = filtered.filter(
        (cat) => cat.display?.isActive === (localFilters.isActive === "true")
      );
    }

    // Parent filter
    if (localFilters.parentId !== "") {
      if (localFilters.parentId === "root") {
        filtered = filtered.filter((cat) => !cat.parentId);
      } else {
        filtered = filtered.filter(
          (cat) => cat.parentId?._id === localFilters.parentId
        );
      }
    }

    // Featured filter
    if (localFilters.isFeatured !== "") {
      filtered = filtered.filter(
        (cat) =>
          cat.display?.isFeatured === (localFilters.isFeatured === "true")
      );
    }

    // Menu filter
    if (localFilters.showInMenu !== "") {
      filtered = filtered.filter(
        (cat) =>
          cat.display?.showInMenu === (localFilters.showInMenu === "true")
      );
    }

    return filtered;
  }, [categories, searchTerm, localFilters]);

  // Handle single actions
  const handleSingleAction = useCallback(
    async (categoryId, action) => {
      const category = categories.find((cat) => cat._id === categoryId);

      switch (action) {
        case "edit":
          setCurrentCategory(category);
          setShowModal(true);
          break;

        case "delete":
          setDeleteModal({ show: true, category });
          break;

        case "view":
          setDetailViewModal({ show: true, categoryId });
          break;

        case "syncStats":
          dispatch(syncCategoryStatsThunk(categoryId)).then((response) => {
            if (!response.error) {
              notifySuccess("Statistics synchronized successfully");
              dispatch(getAllCategoriesThunk());
            }
          });
          break;

        case "moveToRoot":
          dispatch(
            updateCategoryByIdThunk({
              categoryId,
              categoryData: { parentId: null },
            })
          ).then((response) => {
            if (!response.error) {
              notifySuccess("Category moved to root");
              dispatch(getAllCategoriesThunk());
            }
          });
          break;

        case "add":
          // Open create modal with parent pre-selected
          setCurrentCategory({
            _parentId: categoryId,
            _isNewSubcategory: true,
          });
          setShowModal(true);
          break;

        default:
          break;
      }
    },
    [dispatch, categories]
  );

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(
    (reassignTo = null) => {
      if (deleteModal.category) {
        dispatch(
          deleteCategoryByIdThunk({
            categoryId: deleteModal.category._id,
            reassignTo,
          })
        ).then((response) => {
          if (!response.error) {
            notifySuccess("Category deleted successfully");
            dispatch(getAllCategoriesThunk());
            setDeleteModal({ show: false, category: null });
          }
        });
      }
    },
    [dispatch, deleteModal.category]
  );

  // Handle create category
  const handleCreateCategory = useCallback(() => {
    setCurrentCategory(null);
    setShowModal(true);
  }, []);

  // Handle form submit
  const handleFormSubmit = useCallback(
    (categoryData) => {
      // Check if this is a new subcategory creation
      const isNewSubcategory = currentCategory?._isNewSubcategory;

      const thunk =
        currentCategory && !isNewSubcategory
          ? updateCategoryByIdThunk({
              categoryId: currentCategory._id,
              categoryData,
            })
          : createCategoryThunk(categoryData);

      dispatch(thunk).then((response) => {
        if (!response.error) {
          notifySuccess(
            currentCategory && !isNewSubcategory
              ? "Category updated successfully"
              : "Category created successfully"
          );
          dispatch(getAllCategoriesThunk());
          setShowModal(false);
        }
      });
    },
    [currentCategory, dispatch]
  );

  // Handle sync all stats
  const handleSyncAllStats = useCallback(() => {
    dispatch(syncCategoryStatsThunk()).then((response) => {
      if (!response.error) {
        notifySuccess("All statistics synchronized");
        dispatch(getAllCategoriesThunk());
      }
    });
  }, [dispatch]);

  // Load tree view
  const handleLoadTreeView = useCallback(() => {
    setViewMode("tree");
    dispatch(getCategoryTreeThunk());
  }, [dispatch]);

  // Apply filters
  const handleApplyFilters = useCallback(() => {
    dispatch(setFilters(localFilters));
    setShowFilters(false);
  }, [dispatch, localFilters]);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    setLocalFilters({
      isActive: "",
      parentId: "",
      isFeatured: "",
      showInMenu: "",
    });
    dispatch(resetFilters());
    setSearchTerm("");
  }, [dispatch]);

  // Get parent categories for filter dropdown
  const parentCategories = useMemo(() => {
    return categories.filter((cat) => cat.parentLevel === 0);
  }, [categories]);

  // Get modal title based on current category state
  const getModalTitle = useCallback(() => {
    if (!currentCategory) return "Create Category";
    if (currentCategory._isNewSubcategory) {
      const parentCat = categories.find(
        (c) => c._id === currentCategory._parentId
      );
      return `Add Subcategory to "${parentCat?.name || "Category"}"`;
    }
    return "Edit Category";
  }, [currentCategory, categories]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(localFilters).filter((v) => v !== "").length;
  }, [localFilters]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Category Management
              </h1>
              <p className="text-gray-500 mt-1 text-sm sm:text-base">
                Organize and manage your product categories
              </p>
            </div>

            {/* Quick Stats - Desktop */}
            <div className="hidden sm:flex items-center gap-3">
              <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100">
                <div className="flex items-center gap-2">
                  <ChartBarIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Total:</span>
                  <span className="font-semibold text-gray-900">
                    {stats?.totalCategories || categories.length}
                  </span>
                </div>
              </div>
              <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-sm text-gray-500">Active:</span>
                  <span className="font-semibold text-gray-900">
                    {stats?.activeCategories || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats - Mobile */}
          <div className="flex sm:hidden items-center gap-4 mt-3 text-sm">
            <div className="flex items-center gap-1.5 text-gray-600">
              <ChartBarIcon className="w-4 h-4" />
              <span>Total: {stats?.totalCategories || categories.length}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span>Active: {stats?.activeCategories || 0}</span>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          {/* Main Toolbar Row */}
          <div className="flex flex-col gap-4">
            {/* Search and View Toggle Row */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                {/* View Mode Toggle */}
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 rounded-md transition-all ${
                      viewMode === "list"
                        ? "bg-white shadow-sm text-primary"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    title="List View"
                  >
                    <ViewListIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleLoadTreeView}
                    className={`p-2 rounded-md transition-all ${
                      viewMode === "tree"
                        ? "bg-white shadow-sm text-primary"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    title="Tree View"
                  >
                    <FolderOpenIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 rounded-md transition-all ${
                      viewMode === "grid"
                        ? "bg-white shadow-sm text-primary"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    title="Grid View"
                  >
                    <ViewGridIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Filter Button */}
                <Button
                  variant="secondary"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 relative"
                >
                  <FilterIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>

                {/* Sync Stats - Hidden on mobile */}
                <Button
                  variant="secondary"
                  onClick={handleSyncAllStats}
                  disabled={loading}
                  className="hidden sm:flex items-center gap-2"
                >
                  <RefreshIcon
                    className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  />
                  <span>Sync</span>
                </Button>

                {/* Create Button */}
                <Button
                  variant="primary"
                  onClick={handleCreateCategory}
                  className="flex items-center gap-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Create Category</span>
                  <span className="sm:hidden">Create</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Dropdown
                  label="Status"
                  options={[
                    { _id: "", name: "All Status" },
                    { _id: "true", name: "Active" },
                    { _id: "false", name: "Inactive" },
                  ]}
                  selected={localFilters.isActive}
                  onChange={(value) =>
                    setLocalFilters((prev) => ({ ...prev, isActive: value }))
                  }
                  size="small"
                />

                <Dropdown
                  label="Parent Category"
                  options={[
                    { _id: "", name: "All Categories" },
                    { _id: "root", name: "Root Categories" },
                    ...parentCategories.map((cat) => ({
                      _id: cat._id,
                      name: cat.name,
                    })),
                  ]}
                  selected={localFilters.parentId}
                  onChange={(value) =>
                    setLocalFilters((prev) => ({ ...prev, parentId: value }))
                  }
                  size="small"
                />

                <Dropdown
                  label="Featured"
                  options={[
                    { _id: "", name: "All" },
                    { _id: "true", name: "Featured" },
                    { _id: "false", name: "Not Featured" },
                  ]}
                  selected={localFilters.isFeatured}
                  onChange={(value) =>
                    setLocalFilters((prev) => ({ ...prev, isFeatured: value }))
                  }
                  size="small"
                />

                <Dropdown
                  label="Menu Visibility"
                  options={[
                    { _id: "", name: "All" },
                    { _id: "true", name: "In Menu" },
                    { _id: "false", name: "Not in Menu" },
                  ]}
                  selected={localFilters.showInMenu}
                  onChange={(value) =>
                    setLocalFilters((prev) => ({ ...prev, showInMenu: value }))
                  }
                  size="small"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 justify-end mt-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleResetFilters}
                  className="w-full sm:w-auto"
                >
                  Reset Filters
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApplyFilters}
                  className="w-full sm:w-auto"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Results Summary */}
        {(searchTerm || activeFilterCount > 0) && (
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {filteredCategories.length} of {categories.length}{" "}
              categories
            </p>
            {(searchTerm || activeFilterCount > 0) && (
              <button
                onClick={handleResetFilters}
                className="text-sm text-primary hover:text-primary-dark"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Content Area */}
        {loading ? (
          <div className="flex flex-col justify-center items-center h-64 bg-white rounded-xl shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-500">Loading categories...</p>
          </div>
        ) : (
          <>
            {/* List View */}
            {viewMode === "list" && (
              <div className="space-y-3">
                {filteredCategories.map((category) => (
                  <CategoryCard
                    key={category._id}
                    category={category}
                    handleSingleAction={handleSingleAction}
                    variant="list"
                    showStats={true}
                  />
                ))}
              </div>
            )}

            {/* Grid View */}
            {viewMode === "grid" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredCategories.map((category) => (
                  <CategoryCard
                    key={category._id}
                    category={category}
                    handleSingleAction={handleSingleAction}
                    variant="grid"
                    showStats={true}
                  />
                ))}
              </div>
            )}

            {/* Tree View */}
            {viewMode === "tree" && (
              <CategoryTreeView
                categories={categories}
                onAction={handleSingleAction}
                onReorder={() => dispatch(getAllCategoriesThunk())}
              />
            )}

            {/* Empty State */}
            {filteredCategories.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm p-8 sm:p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FolderOpenIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No categories found
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  {searchTerm || activeFilterCount > 0
                    ? "Try adjusting your search or filters to find what you're looking for."
                    : "Get started by creating your first category."}
                </p>
                {searchTerm || activeFilterCount > 0 ? (
                  <Button
                    variant="primary-outline"
                    onClick={handleResetFilters}
                  >
                    Clear Filters
                  </Button>
                ) : (
                  <Button variant="primary" onClick={handleCreateCategory}>
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Create Category
                  </Button>
                )}
              </div>
            )}
          </>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <Modal
            isOpen={true}
            closeModal={() => setShowModal(false)}
            title={getModalTitle()}
            maxWidth="4xl"
          >
            <CategoryForm
              category={currentCategory}
              onSubmit={handleFormSubmit}
              onCancel={() => setShowModal(false)}
              serverError={error}
              defaultParentId={currentCategory?._parentId}
            />
          </Modal>
        )}

        {/* Detail View Modal */}
        {detailViewModal.show && (
          <CategoryDetailView
            onEdit={(category) => {
              setDetailViewModal({ show: false, categoryId: null });
              setCurrentCategory(category);
              setShowModal(true);
            }}
            isOpen={true}
            categoryId={detailViewModal.categoryId}
            onClose={() =>
              setDetailViewModal({ show: false, categoryId: null })
            }
          />
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal.show && (
          <Modal
            isOpen={true}
            closeModal={() => setDeleteModal({ show: false, category: null })}
            title="Delete Category"
            size="medium"
          >
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FolderOpenIcon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-gray-900">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold">
                      "{deleteModal.category?.name}"
                    </span>
                    ?
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              {deleteModal.category?.productCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <p className="text-sm text-amber-800">
                    ⚠️ This category has{" "}
                    <span className="font-semibold">
                      {deleteModal.category.productCount} products
                    </span>
                    . They will become uncategorized after deletion.
                  </p>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
                <Button
                  variant="secondary"
                  onClick={() =>
                    setDeleteModal({ show: false, category: null })
                  }
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleDeleteConfirm()}
                  className="w-full sm:w-auto"
                >
                  Delete Category
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
